import { useState, useEffect, useCallback } from "react";
import "./../../assets/css/views/subRetailManagement/posManagement.css";
import {
  Button,
  Checkbox,
  IconHelp,
  Modal,
  Select,
  TextBox,
} from "@veneer/core";
import MapComponent from "../../components/Maps/MapComponent";
import { useTranslation } from "react-i18next";
import PointofSalesLogic from "./PointofSalesLogic";
import { FormFields, IPosIn } from "../../models/input/agendaApi/IPosIn";
import LoaderModal from "../../components/Loader/loaderModal";
import { useLocation } from "react-router-dom";
import { decryptText } from "../../utils/encryptionUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

export default function UpdatePOS() {
  const state: any = useSelector((state: RootState) => state.user);
  const location = useLocation();
  const [coordinates, setCoordinates] = useState<google.maps.LatLngLiteral>({
    lat: 0,
    lng: 0,
  });
  const [markers, setMarkers] = useState<
    { position: google.maps.LatLngLiteral; title: string }[]
  >([]);
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<number>(0);
  const [messageModal, setMessageModal] = useState({
    title: "",
    message: "",
  });
  const [openModal, setOpenModal] = useState(false);
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");
  const [isBrasil, setIsBrasil] = useState(false);
  const [checkOnline, setCheckOnline] = useState(false);
  const [t] = useTranslation("global");
  const {
    getSubRetail,
    getClassList,
    getCountryList,
    getStatesList,
    getCityList,
    getStratificationList,
    updatePOS,
    dataSubRetailList,
    dataClassList,
    dataCountryList,
    dataStateList,
    dataCityList,
    dataStratificationList,
    loading,
    formPointOfSaleUpdate,
    setFormPointOfSaleUpdate,
    currentDataSelect,
    handleChangeSearch,
    setDataCityList,
    setDataClassList,
    setDataCountryList,
    setDataStateList,
    setDataStratificationList,
  } = PointofSalesLogic();
  const dataUpdated = location.state;
  useEffect(() => {
    getSubRetail();
    getCountryList();
    getClassList();
    getStratificationList();
    setIsBrasil(
      [13, 28, 47, 63, 65].includes(Number(decryptText(state.user.countryId)))
    );
  }, []);

  useEffect(() => {
    setFormPointOfSaleUpdate(dataUpdated?.newData);
    setCheckOnline(dataUpdated.data?.online);
    getStatesList(dataUpdated.data?.idCountry);
    getCityList(dataUpdated.data?.idState);
    setLatitude(!dataUpdated.data?.online ? dataUpdated.data?.latitude : "");
    setLongitude(!dataUpdated.data?.online ? dataUpdated.data?.longitude : "");
    setLat(!dataUpdated.data?.online ? dataUpdated.data?.latitude : "");
    setLng(!dataUpdated.data?.online ? dataUpdated.data?.longitude : "");
    const newCoordinates = {
      lat: Number(dataUpdated.data?.latitude ?? 0),
      lng: Number(dataUpdated.data?.longitude ?? 0),
    };
    setCoordinates(newCoordinates);
    setMarkers([{ position: newCoordinates, title: "Clicked Location" }]);
  }, [dataUpdated]);

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    const { latLng } = event;
    if (latLng) {
      const newCoordinates = { lat: latLng.lat(), lng: latLng.lng() };
      setCoordinates(newCoordinates);
      setMarkers([{ position: newCoordinates, title: "Clicked Location" }]);
      setLat(newCoordinates.lat);
      setLng(newCoordinates.lng);
      setLatitude(newCoordinates.lat.toString());
      setLongitude(newCoordinates.lng.toString());
    }
  };
  const handleLatChange = (e: string) => {
    setLatitude(e);
  };

  const handleLngChange = (e: string) => {
    setLongitude(e);
  };

  const isFormComplete = (): boolean => {
    const { cnpj, ...fieldsToCheck } = formPointOfSaleUpdate;
    const optionalFieldsWhenOnline = new Set([
      "countryId",
      "stateId",
      "cityId",
      "longitude",
      "latitude",
      "economyId",
    ]);
    let allFieldsComplete = true;

    for (const [key, value] of Object.entries(fieldsToCheck)) {
      if (optionalFieldsWhenOnline.has(key) && checkOnline) {
        continue;
      }
      if (value.length === 0) {
        allFieldsComplete = false;
        break;
      }
    }

    if (!checkOnline && (latitude === "" || longitude === "")) {
      allFieldsComplete = false;
    }

    const isCNPJComplete = checkOnline ? true : allFieldsComplete;
    return allFieldsComplete && isCNPJComplete;
  };

  const handleSaveLocation = () => {
    setLat(Number(latitude));
    setLng(Number(longitude));
    const newCoordinates = { lat: Number(latitude), lng: Number(longitude) };
    setCoordinates(newCoordinates);
    setMarkers([{ position: newCoordinates, title: "Saved Location" }]);
  };

  const onChangeForm = useCallback(
    (option: { value: any }, type: keyof FormFields) => {
      setFormPointOfSaleUpdate((prev) => {
        const isEmptyValue = option.value === "";
        const updatedForm = {
          ...prev,
          [type]: isEmptyValue ? [] : [option.value],
        };

        if (isEmptyValue) {
          if (type === "countryId") {
            updatedForm.stateId = [];
            updatedForm.cityId = [];
          } else if (type === "stateId") {
            updatedForm.cityId = [];
          }
        } else {
          switch (type) {
            case "countryId":
              updatedForm.stateId = [];
              updatedForm.cityId = [];
              getStatesList(option.value);
              break;
            case "stateId":
              updatedForm.cityId = [];
              getCityList(option.value);
              break;
            default:
              break;
          }
        }
        return updatedForm;
      });
    },
    [getStatesList, getCityList]
  );

  const clearfieldFromForm = (field: keyof FormFields) => {
    setFormPointOfSaleUpdate((prev: FormFields) => {
      const newState: Partial<FormFields> = { [field]: [] };
      if (field === "countryId") {
        newState.stateId = [];
        newState.cityId = [];
      } else if (field === "stateId") {
        newState.cityId = [];
      }
      return { ...prev, ...newState };
    });
  };

  const getModalCode = () => {
    setOpenModal(true);
    setMessageModal({
      title: t("posManagement.posCode"),
      message: t("posManagement.msgPosCode"),
    });
  };

  const getModalPointSale = () => {
    setOpenModal(true);
    setMessageModal({
      title: t("posManagement.pointOfSale"),
      message: t("posManagement.msgPointOfSale"),
    });
  };

  const isEnabled = (type: string[]) => {
    if (checkOnline || type.length === 0) {
      return true;
    } else {
      return false;
    }
  };

  const createPOS = () => {
    const params: IPosIn = {
      userID: 0,
      subRetailID: formPointOfSaleUpdate.subretailId[0]
        ? Number(formPointOfSaleUpdate.subretailId[0])
        : 0,
      pos: formPointOfSaleUpdate.pointOfSale[0] ?? null,
      poS_ClassID: formPointOfSaleUpdate.claseId[0]
        ? Number(formPointOfSaleUpdate.claseId[0])
        : 0,
      onLine: checkOnline ? 1 : 0,
      countryID: checkOnline
        ? 0
        : Number(formPointOfSaleUpdate.countryId[0]) ?? 0,
      stateID: checkOnline ? 0 : Number(formPointOfSaleUpdate.stateId[0]) ?? 0,
      cityID: checkOnline ? 0 : Number(formPointOfSaleUpdate.cityId[0]) ?? 0,
      stratificationID: formPointOfSaleUpdate.economyId[0]
        ? Number(formPointOfSaleUpdate.economyId[0])
        : 0,
      longitude: checkOnline ? null : longitude ?? null,
      latitude: checkOnline ? null : latitude ?? null,
      active: 1,
      poS_Code: formPointOfSaleUpdate.posCode[0] ?? null,
      cnpj: formPointOfSaleUpdate.cnpj[0] ?? null,
      address: "",
    };
    updatePOS(params);
  };

  return (
    <div className="generalPOSDiv">
      <div className="posManagementTopElements">
        <div className="divForm">
          <h4 className="title">{t("posManagement.updatePOS")}</h4>
          <div className="form">
            <Select
              className="selectGeneralPOS"
              options={dataSubRetailList}
              id="select-usage"
              label={t("posManagement.subRetail")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "subretailId")}
              onClear={() => clearfieldFromForm("subretailId")}
              value={[Number(formPointOfSaleUpdate.subretailId[0])]}
              disabled={true}
            />
             <div className="containerWithIconPOS">
              <TextBox
                className="posManagementTextbox"
                label={t("posManagement.posCode")}
                id="posCode"
                type="text"
                value={formPointOfSaleUpdate.posCode[0] ?? ""}
                maxLength={100}
                onChange={(value) => onChangeForm({ value: value }, "posCode")}
                disabled
              />
              <Button
                className="helpButtonPOS"
                leadingIcon={<IconHelp />}
                onClick={getModalCode}>
              </Button>
            </div>
            <div className="containerWithIconPOS">
              <TextBox
                className="posManagementTextbox"
                label={t('posManagement.pointOfSale')}
                id="pointOfSale"
                type="text"
                value={formPointOfSaleUpdate.pointOfSale[0] ?? ""}
                maxLength={100}
                onChange={(value) =>
                  onChangeForm({ value: value }, "pointOfSale")
                }
              />
              <Button
                className="helpButtonPOS"
                leadingIcon={<IconHelp />}
                onClick={getModalPointSale}
              ></Button>
            </div>
            <div className="containerClassPOS">
              <Select
                className="selectGeneralPOS"
                options={dataClassList}
                id="select-usage"
                label={t("posManagement.class")}
                placement="bottom"
                helperTextVisibility="auto"
                onChange={(value) => onChangeForm(value, "claseId")}
                onClear={() => clearfieldFromForm("claseId")}
                value={[Number(formPointOfSaleUpdate.claseId)]}
                disabled={false}
                showSearch
                onSearch={(value) =>
                  handleChangeSearch(
                    value,
                    currentDataSelect.dataClassList,
                    setDataClassList
                  )
                }
              />
              <div className="checkContainerPOS">
                <Checkbox
                  label={t("header.online")}
                  className="registerSalePriceCheckbox"
                  id="checkbox"
                  value={checkOnline}
                  checked={checkOnline}
                  onChange={() => setCheckOnline((prev) => !prev)}
                />
              </div>
            </div>
            <Select
              className="selectGeneralPOS"
              options={dataCountryList}
              id="select-usage"
              label={t("header.country")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "countryId")}
              onClear={() => clearfieldFromForm("countryId")}
              value={[Number(formPointOfSaleUpdate.countryId[0])]}
              disabled={checkOnline}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataCountryList,
                  setDataCountryList
                )
              }
            />
            <Select
              className="selectGeneralPOS"
              options={dataStateList}
              id="select-usage"
              label={t("header.state")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "stateId")}
              onClear={() => clearfieldFromForm("stateId")}
              value={[Number(formPointOfSaleUpdate.stateId)]}
              disabled={isEnabled(formPointOfSaleUpdate.countryId)}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataStateList,
                  setDataStateList
                )
              }
            />
            <Select
              className="selectGeneralPOS"
              options={dataCityList}
              id="select-usage"
              label={t("posManagement.city")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "cityId")}
              onClear={() => clearfieldFromForm("cityId")}
              value={[Number(formPointOfSaleUpdate.cityId)]}
              disabled={isEnabled(formPointOfSaleUpdate.stateId)}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataCityList,
                  setDataCityList
                )
              }
            />
            <Select
              className="selectGeneralPOS"
              options={dataStratificationList}
              id="select-usage"
              label={t("posManagement.stratification")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "economyId")}
              onClear={() => clearfieldFromForm("economyId")}
              value={[Number(formPointOfSaleUpdate.economyId)]}
              disabled={checkOnline}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataStratification,
                  setDataStratificationList
                )
              }
            />
            {isBrasil && (
              <TextBox
                label={t("posManagement.cnpj")}
                id="CNPJ"
                className="posManagementTextbox"
                type="text"
                value={formPointOfSaleUpdate.cnpj[0] ?? ""}
                maxLength={100}
                onChange={(value) => onChangeForm({ value: value }, "cnpj")}
              />
            )}
            <div className="divButtonAddPOS">
              <Button
                className="buttonAddPOS"
                onClick={createPOS}
                disabled={!isFormComplete()}
              >
                {t("header.save")}
              </Button>
            </div>
          </div>
        </div>
        <div className="divMapCoordenatesPOS">
          <div className="posManagementDivMap">
            <MapComponent
              center={coordinates}
              zoom={13}
              markers={markers}
              onMapClick={handleMapClick}
            />
          </div>
          <div className="coordinates-inputs-pos">
            <TextBox
              label={t("posManagement.latitude")}
              id="latitude"
              type="number"
              className="textbox"
              value={latitude.toString()}
              onChange={(e) => handleLatChange(e)}
              disabled={checkOnline}
            />
            <TextBox
              label={t("posManagement.longitude")}
              id="longitude"
              className="textbox"
              type="number"
              maxLength={100}
              value={longitude.toString()}
              onChange={(e) => handleLngChange(String(e))}
              disabled={checkOnline}
            />
            <div>
            <Button
              onClick={handleSaveLocation}
              disabled={lng != 0 && lat != 0 && !checkOnline ? false : true}
            >
              {t("header.search")}
            </Button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        align="start"
        id="modal-id"
        show={openModal}
        onClose={() => {}}
        footer={
          <div style={{ textAlign: "center" }}>
            <Button
              style={{ marginRight: 4 }}
              onClick={() => setOpenModal(false)}
            >
              {t("header.confirm")}
            </Button>
          </div>
        }
        title={messageModal.title}
      >
        <p>{messageModal.message}</p>
      </Modal>
      {loading && (
        <div>
          <LoaderModal>
            <div> {t("modal.savingInformation")}</div>
          </LoaderModal>
        </div>
      )}
    </div>
  );
}
